import { createSelector } from "@reduxjs/toolkit";
import { skipToken } from "@reduxjs/toolkit/query";
import { BUILT_IN_VIEW_PRESETS, ZViewPresetMongoDocument, type IViewPreset } from "@multiconsult-group/multiplacer-core";
import { openapi, type GetMultiplacerViewsApiArg } from "../../openapi";
import type { IRootState } from "../app/store";
import type { IViewPresetReference } from "../ViewerRedux/viewerSlice";
import { selectViewerView } from "./viewSelectors";
import { viewerViewFingerprint } from "./viewerViewFingerprint";

// Use these exact arguments for both the generated hook and cache selectors.
export const selectViewPresetsQueryArgs = createSelector(
  [(state: IRootState) => state.design.projectNo],
  (projectNo): GetMultiplacerViewsApiArg | typeof skipToken =>
    projectNo ? { projectNo } : skipToken,
);

const selectCacheSelector = createSelector(
  [selectViewPresetsQueryArgs],
  args => openapi.endpoints.getMultiplacerViews.select(args),
);
const selectCachedPresets = (state: IRootState) => selectCacheSelector(state)(state).data;

export const selectSavedViewPresets = createSelector(
  [selectCachedPresets],
  presets => (presets ?? []).map(preset => ZViewPresetMongoDocument.required({ _id: true }).parse(preset)),
);

export const selectBuiltInViewPresets = () => BUILT_IN_VIEW_PRESETS;

export const selectViewPresetListCategory = (state: IRootState) => state.viewer.viewPresetListCategory;

const selectActiveModule = (state: IRootState) => state.design.activeModule;

// Filtering the list must not change the selected preset or its comparison baseline.
export const selectListedViewPresets = createSelector(
  [selectViewPresetListCategory, selectBuiltInViewPresets, selectSavedViewPresets, selectActiveModule],
  (category, builtIn, saved, activeModule) => category === "builtIn"
    ? builtIn.filter(preset => activeModule && preset.modules.includes(activeModule)) : saved.filter(preset => preset.scope === category),
);

const resolvePreset = (reference: IViewPresetReference | null, builtIn: IViewPreset[], saved: IViewPreset[]): IViewPreset | null => {
  if (!reference) return null;
  return (reference.source === "builtIn"
    ? builtIn.find(preset => preset.source === "builtIn" && preset.key === reference.key)
    : saved.find(preset => preset.source === "user" && preset._id === reference.id)) ?? null;
};

export const selectSelectedViewPreset = createSelector(
  [(state: IRootState) => state.viewer.selectedViewPreset, selectBuiltInViewPresets, selectSavedViewPresets],
  resolvePreset,
);

export const selectViewSettingsPreset = createSelector(
  [(state: IRootState) => state.viewer.viewSettingsPreset, selectBuiltInViewPresets, selectSavedViewPresets],
  resolvePreset,
);

// Null means no preset is selected, or its definition is unavailable (loading/deleted).
export const selectSelectedViewPresetIsDirty = createSelector(
  [selectSelectedViewPreset, selectViewerView],
  (preset, currentView): boolean | null => preset
    ? viewerViewFingerprint(preset.view) !== viewerViewFingerprint(currentView) : null,
);
